import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { useSectionDetailsStore } from "../../stores/sectionDetails.store";
import "../jobDetails/JobDetails.scss";
import SectionDetailsHeader from "./SectionDetailsHeader";
import SectionDetailsInfo from "./SectionDetailsInfo";
import SectionQuestionsTable from "./SectionQuestionsTable";

const SectionDetails = () => {
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const { section, isLoading, error, load, reset } = useSectionDetailsStore();
  const { getResourceValue } = useResources();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (sectionId) {
      load(sectionId);
    }
    return () => reset();
  }, [sectionId, load, reset]);

  if (isLoading) {
    return (
      <div className="job-details-loading">
        {/* Loading indicator */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-details-error">
        {getResourceValue("error_prefix", "Error:")} {error}
      </div>
    );
  }

  if (!section) {
    return (
      <div className="job-details-not-found">
        {getResourceValue("section_not_found", "Section not found")}
      </div>
    );
  }

  const handlePreview = (questionId: string) => {
    // Navigate to existing preview results page with single question
    navigate(`/preview-questions/results/${questionId}`);
  };

  const handlePreviewAll = () => {
    // Get filtered questions based on search query
    const filteredQuestions = (section?.question_ids || []).filter((questionId) =>
      questionId.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (filteredQuestions.length === 0) {
      return;
    }
    
    // Navigate to existing preview results page with all filtered questions
    navigate("/preview-questions/results", {
      state: {
        questionIds: filteredQuestions,
        source: "sectionDetails"
      }
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  return (
    <div className="page">
      <div className="job-details">
        <SectionDetailsHeader 
          sectionId={section.section_id}
          onPreviewAll={handlePreviewAll}
          previewAllCount={(section.question_ids || []).filter((questionId) =>
            questionId.toLowerCase().includes(searchQuery.toLowerCase())
          ).length}
        />

        <SectionDetailsInfo
          title={section.section_title}
          country={section.country_localization_key}
          language={section.subject_language_iso_code.toUpperCase()}
          grade={getResourceValue(section.grade_localization_key, section.grade_localization_key)}
          subject={section.subject_name}
          term={section.term_name}
        />

        <SectionQuestionsTable
          questionIds={section.question_ids || []}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onPreview={handlePreview}
        />
      </div>
    </div>
  );
};

export default SectionDetails;

